import React, { useState } from 'react';
import { Blaetterleiste, TabellenKopf, useTabelle, type SpaltenDefinition } from '../components/TabellenSteuerung';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Wand2, AlertTriangle, Server, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { baseStacks } from '../api';
import type { BaseStack, StackVariable, VariableSource } from '../api';

/**
 * Managing the base stacks — the infrastructure definitions that get deployed to
 * hosts (reverse proxy, its database, the sync sidecar).
 *
 * The variable editor is the part that earns its space. Docker compose replaces
 * an undeclared `${VAR}` with an empty string *and exits 0*, so a typo starts a
 * database with a blank root password instead of failing. "Analysieren" reads the
 * placeholders straight out of the pasted compose file so none is forgotten.
 */

const SOURCE_LABEL: Record<VariableSource, string> = {
  AUTO: 'Aus dem Server abgeleitet',
  GENERATE: 'Pro Server erzeugen',
  GLOBAL: 'Ein Wert für alle Server',
  PER_SERVER: 'Pro Server eingeben',
  // Der PC ermittelt ihn bei jedem Ausrollen. Kein Eingabefeld, und bewusst
  // nicht "abgeleitet" genannt: ein abgeleiteter Wert bleibt, dieser aendert
  // sich mit den Instanzen des Servers.
  BERECHNET: 'Beim Ausrollen ermittelt',
};

const AUTO_FIELDS = [
  { value: 'hostname', label: 'Hostname vollständig' },
  { value: 'hostnameShort', label: 'Hostname, erster Teil (srv1105512)' },
  { value: 'hostnameSlug', label: 'Hostname als Slug (punkte zu bindestrich)' },
  { value: 'ipAddress', label: 'IP-Adresse' },
  { value: 'region', label: 'Region' },
  { value: 'id', label: 'Server-ID' },
] as const;

interface FormState {
  name: string;
  slug: string;
  description: string;
  version: string;
  status: 'DRAFT' | 'ACTIVE' | 'DEPRECATED';
  composeContent: string;
  envTemplate: string;
  variables: StackVariable[];
  requiredNetworks: string;
  requiredPaths: string;
  isDefault: boolean;
}

const EMPTY: FormState = {
  name: '',
  slug: '',
  description: '',
  version: '1.0.0',
  status: 'DRAFT',
  composeContent: '',
  envTemplate: '',
  variables: [],
  requiredNetworks: '',
  requiredPaths: '',
  isDefault: false,
};

function VariableEditor({
  variables,
  onChange,
}: {
  variables: StackVariable[];
  onChange: (next: StackVariable[]) => void;
}) {
  const { t } = useTranslation();
  const update = (i: number, patch: Partial<StackVariable>) =>
    onChange(variables.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));

  return (
    <div className="space-y-2">
      {variables.length === 0 && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
          Noch keine Variablen. „Analysieren" liest sie aus dem Compose.
        </p>
      )}
      {variables.map((v, i) => (
        <div key={`${v.key}-${i}`} className="rounded-lg border border-slate-200 p-3">
          <div className="grid grid-cols-12 gap-2">
            <input
              value={v.key}
              onChange={(e) => update(i, { key: e.target.value })}
              placeholder="MYSQL_PASSWORD"
              className="col-span-4 rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
            />
            <select
              value={v.source}
              onChange={(e) => update(i, { source: e.target.value as VariableSource })}
              className="col-span-5 rounded border border-slate-300 px-2 py-1.5 text-xs"
            >
              {(Object.keys(SOURCE_LABEL) as VariableSource[]).map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABEL[s]}
                </option>
              ))}
            </select>
            <label className="col-span-2 flex items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={v.secret ?? false}
                onChange={(e) => update(i, { secret: e.target.checked })}
              />
              Geheim
            </label>
            <button
              type="button"
              onClick={() => onChange(variables.filter((_, idx) => idx !== i))}
              className="col-span-1 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
              title="Entfernen"
            >
              <Trash2 size={13} />
            </button>
          </div>

          {v.source === 'AUTO' && (
            <select
              value={v.autoField ?? 'hostname'}
              onChange={(e) => update(i, { autoField: e.target.value as StackVariable['autoField'] })}
              className="mt-2 w-full rounded border border-slate-300 px-2 py-1.5 text-xs"
            >
              {AUTO_FIELDS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          )}
          {v.source === 'GLOBAL' && (
            <input
              value={v.value === '__UNCHANGED__' ? '' : v.value ?? ''}
              onChange={(e) => update(i, { value: e.target.value })}
              type={v.secret ? 'password' : 'text'}
              placeholder={
                v.secret && v.value === '__UNCHANGED__'
                  ? 'gespeichert — leer lassen, um ihn zu behalten'
                  : 'Wert für alle Server'
              }
              className="mt-2 w-full rounded border border-slate-300 px-2 py-1.5 text-xs"
            />
          )}
          {v.source === 'PER_SERVER' && (
            <input
              value={v.default ?? ''}
              onChange={(e) => update(i, { default: e.target.value })}
              placeholder={t('baseStacks.suggestionOverridable')}
              className="mt-2 w-full rounded border border-slate-300 px-2 py-1.5 text-xs"
            />
          )}
          {v.source !== 'AUTO' && (
            <p className="mt-1.5 text-[11px] text-slate-400">
              {v.source === 'GENERATE'
                ? 'Wird pro Server einmal erzeugt und gemerkt — ein Redeploy behält den Wert.'
                : 'Jeder Server kann diesen Wert im Deploy-Dialog überschreiben.'}
            </p>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...variables, { key: '', source: 'PER_SERVER' }])}
        className="text-xs font-medium text-brand-600 hover:text-brand-800"
      >
        + Variable
      </button>
    </div>
  );
}

export default function BaseStacksPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState<{
    placeholders: string[];
    ports: number[];
    networks: string[];
  } | null>(null);

  const { data: stacks = [], isLoading } = useQuery({
    queryKey: ['base-stacks'],
    queryFn: baseStacks.list,
  });

  const spalten: SpaltenDefinition<(typeof stacks)[number]>[] = [
    { key: 'name', label: t('common.name'), wert: (s) => s.name },
    { key: 'slug', label: t('baseStacks.slug'), wert: (s) => s.slug },
    { key: 'version', label: t('releases.version'), wert: (s) => s.version ?? '' },
    { key: 'status', label: t('common.status'), wert: (s) => s.status },
    { key: 'ports', label: t('baseStacks.ports'), wert: (s) => (s.declaredPorts ?? []).length },
    { key: 'server', label: t('tables.server'), wert: (s) => (s.deployments ?? []).length },
  ];

  const tabelle = useTabelle({
    speichern: 'base-stacks',
    zeilen: stacks,
    spalten,
    startSortierung: { key: 'name', richtung: 'auf' },
  });


  const analyse = useMutation({
    mutationFn: () => baseStacks.analyse(form.composeContent),
    onSuccess: (result) => {
      setAnalysis(result);
      // Pre-fill anything not yet declared. Password-looking names default to
      // GENERATE+secret because that is right far more often than not.
      const known = new Set(form.variables.map((v) => v.key));
      const added: StackVariable[] = result.placeholders
        .filter((k) => !known.has(k))
        .map((key) => {
          const secretish = /PASS|SECRET|TOKEN|KEY/i.test(key);
          if (/SERVERNAME|HOSTNAME|INSTANCE/i.test(key)) {
            return { key, source: 'AUTO' as VariableSource, autoField: 'hostnameShort' as const };
          }
          return secretish
            ? { key, source: 'GENERATE' as VariableSource, secret: true }
            : { key, source: 'PER_SERVER' as VariableSource };
        });
      if (added.length) setForm((f) => ({ ...f, variables: [...f.variables, ...added] }));
      setForm((f) => ({
        ...f,
        requiredNetworks: f.requiredNetworks || result.networks.join(', '),
      }));
    },
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name,
        slug: form.slug,
        description: form.description || null,
        version: form.version,
        status: form.status,
        composeContent: form.composeContent,
        envTemplate: form.envTemplate || null,
        variables: form.variables.filter((v) => v.key.trim()),
        requiredNetworks: form.requiredNetworks.split(',').map((n) => n.trim()).filter(Boolean),
        requiredPaths: form.requiredPaths.split('\n').map((p) => p.trim()).filter(Boolean),
        isDefault: form.isDefault,
      };
      return editingId ? baseStacks.update(editingId, payload) : baseStacks.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['base-stacks'] });
      setShowEditor(false);
      setEditingId(null);
      setForm(EMPTY);
      setAnalysis(null);
      setError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message ?? 'Speichern fehlgeschlagen');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => baseStacks.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['base-stacks'] }),
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } };
      alert(e.response?.data?.message ?? 'Löschen fehlgeschlagen');
    },
  });

  function startEdit(stack: BaseStack) {
    setForm({
      name: stack.name,
      slug: stack.slug,
      description: stack.description ?? '',
      version: stack.version,
      status: stack.status,
      composeContent: stack.composeContent,
      envTemplate: stack.envTemplate ?? '',
      variables: stack.variables ?? [],
      requiredNetworks: (stack.requiredNetworks ?? []).join(', '),
      requiredPaths: (stack.requiredPaths ?? []).join('\n'),
      isDefault: stack.isDefault,
    });
    setEditingId(stack.id);
    setAnalysis(null);
    setError('');
    setShowEditor(true);
  }

  const undeclared = analysis
    ? analysis.placeholders.filter((p) => !form.variables.some((v) => v.key === p))
    : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {stacks.length} Base-Stack{stacks.length === 1 ? '' : 's'} — Infrastruktur, die auf
          Hosts ausgerollt wird
        </p>
        <button
          onClick={() => {
            setForm(EMPTY);
            setEditingId(null);
            setAnalysis(null);
            setShowEditor(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm text-white hover:bg-brand-700"
        >
          <Plus size={14} /> Base-Stack anlegen
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-surface">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <TabellenKopf spalten={spalten} steuerung={tabelle} extra={[t('common.actions')]} />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  <Loader2 size={16} className="mx-auto animate-spin" />
                </td>
              </tr>
            ) : stacks.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center italic text-slate-400">
                  Noch keine Base-Stacks. Compose einfügen, Variablen deklarieren, fertig.
                </td>
              </tr>
            ) : (
              stacks.map((stack) => (
                <tr key={stack.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{stack.name}</div>
                    {stack.description && (
                      <div className="max-w-md truncate text-xs text-slate-500">
                        {stack.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{stack.slug}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{stack.version}</td>
                  <td className="px-4 py-3">
                    <span
                      className={clsx(
                        'rounded px-1.5 py-0.5 text-xs font-medium',
                        stack.status === 'ACTIVE'
                          ? 'bg-emerald-50 text-emerald-700'
                          : stack.status === 'DRAFT'
                            ? 'bg-slate-100 text-slate-600'
                            : 'bg-amber-50 text-amber-700',
                      )}
                    >
                      {stack.status}
                    </span>
                    {stack.isDefault && (
                      <span className="ml-1 rounded bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700">
                        Standard
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">
                    {(stack.declaredPorts ?? []).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1">
                      <Server size={12} /> {stack.deployments?.length ?? 0}
                    </span>
                  </td>
                  <td className="space-x-2 px-4 py-3 text-xs">
                    <button
                      onClick={() => startEdit(stack)}
                      className="font-medium text-brand-600 hover:text-brand-800"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Base-Stack "${stack.name}" löschen?`)) remove.mutate(stack.id);
                      }}
                      className="font-medium text-red-500 hover:text-red-700"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Blaetterleiste blaettern={tabelle.blaettern} />
      {showEditor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
          <div className="mt-6 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-base font-semibold">
                {editingId ? 'Base-Stack bearbeiten' : 'Base-Stack anlegen'}
              </h2>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">{t('forms.nameRequired')}</label>
                  <input
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        name: e.target.value,
                        // Slug follows the name until it has been touched — it
                        // becomes the compose project name on the host.
                        slug: editingId
                          ? f.slug
                          : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
                      }))
                    }
                    placeholder="Nginx Proxy Manager"
                    className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">{t('forms.slugRequired')}</label>
                  <input
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    placeholder="nginx-proxy-manager"
                    className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-sm"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">
                    Wird Compose-Projektname und Verzeichnis auf dem Host.
                  </p>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Version</label>
                  <input
                    value={form.version}
                    onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
                    className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-sm"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">
                    Hochzählen — Hosts mit älterer Version werden markiert.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, status: e.target.value as FormState['status'] }))
                    }
                    className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  >
                    <option value="DRAFT">{t('baseStacks.statusDraft')}</option>
                    <option value="ACTIVE">{t('baseStacks.statusActive')}</option>
                    <option value="DEPRECATED">{t('baseStacks.statusDeprecated')}</option>
                  </select>
                </div>
                <label className="col-span-2 mt-6 flex items-start gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.isDefault}
                    onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
                    className="mt-0.5"
                  />
                  <span>
                    Bei neuen Servern vorausgewählt
                    <span className="block text-[11px] text-slate-400">
                      Der Haken im Server-Dialog ist dann schon gesetzt.
                    </span>
                  </span>
                </label>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Beschreibung
                </label>
                <input
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                />
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-600">
                    docker-compose.yaml *
                  </label>
                  <button
                    type="button"
                    onClick={() => analyse.mutate()}
                    disabled={form.composeContent.length < 20 || analyse.isPending}
                    className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800 disabled:opacity-40"
                  >
                    {analyse.isPending ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Wand2 size={12} />
                    )}
                    Analysieren
                  </button>
                </div>
                <textarea
                  value={form.composeContent}
                  onChange={(e) => setForm((f) => ({ ...f, composeContent: e.target.value }))}
                  rows={14}
                  spellCheck={false}
                  placeholder={'services:\n  nginx-proxy-manager:\n    image: jc21/nginx-proxy-manager:latest\n    …'}
                  className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
                />
              </div>

              {analysis && (
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded border border-slate-200 bg-slate-50 p-2">
                    <div className="font-medium text-slate-600">Platzhalter</div>
                    <div className="font-mono text-slate-800">
                      {analysis.placeholders.join(', ') || '—'}
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 bg-slate-50 p-2">
                    <div className="font-medium text-slate-600">Host-Ports</div>
                    <div className="font-mono text-slate-800">
                      {analysis.ports.join(', ') || '—'}
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 bg-slate-50 p-2">
                    <div className="font-medium text-slate-600">{t('baseStacks.externalNetworks')}</div>
                    <div className="font-mono text-slate-800">
                      {analysis.networks.join(', ') || '—'}
                    </div>
                  </div>
                </div>
              )}

              {undeclared.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <div>
                    <span className="font-medium">
                      Nicht deklariert: {undeclared.join(', ')}
                    </span>
                    <p className="mt-1">
                      Docker Compose setzt für nicht gesetzte Variablen eine leere Zeichenkette
                      ein und startet trotzdem — eine MariaDB bekäme so ein leeres
                      Root-Passwort. Der Deploy bricht deshalb ab, solange das offen ist.
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Variablen</label>
                <VariableEditor
                  variables={form.variables}
                  onChange={(variables) => setForm((f) => ({ ...f, variables }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">
                    Externe Netze (Komma)
                  </label>
                  <input
                    value={form.requiredNetworks}
                    onChange={(e) => setForm((f) => ({ ...f, requiredNetworks: e.target.value }))}
                    placeholder="ai-base"
                    className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">
                    Werden vor dem Start angelegt — ein <code>external: true</code>-Netz lässt
                    <code> up</code> sonst scheitern.
                  </p>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">
                    Verzeichnisse (eine pro Zeile)
                  </label>
                  <textarea
                    value={form.requiredPaths}
                    onChange={(e) => setForm((f) => ({ ...f, requiredPaths: e.target.value }))}
                    rows={3}
                    placeholder={'/Container/nginx-proxy-manager/data\n/Container/nginx-proxy-manager/letsencrypt'}
                    className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                onClick={() => {
                  setShowEditor(false);
                  setEditingId(null);
                }}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600"
              >
                Abbrechen
              </button>
              <button
                onClick={() => save.mutate()}
                disabled={
                  save.isPending ||
                  !form.name.trim() ||
                  !form.slug.trim() ||
                  form.composeContent.length < 20
                }
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-40"
              >
                {save.isPending ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
